import React, { useCallback, useEffect } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";

import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import GridViewIcon from "@mui/icons-material/GridView";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { IconProps, Stack } from "@mui/material";
import { HorizontalNavBar, RoleComponentGuard } from "components";
import { InlineFilter } from "components/Filters/InlineFilter";
import { SelectDateRange } from "components/forms";
import { SetPicker } from "components/reports-cashflow";
import { Routes } from "constants/routes";
import { useActionHook, useModals } from "hooks";
import {
  useAccountSelector,
  useDealActions,
  useReportsActions,
  useReportsFiltersSelector,
  useReportsQueriesSelector
} from "store";
import { DateRange, DateRangeValueType, ReportFilters } from "store/types";

import { ContextMenuReports, GroupByContent, GroupByMenu, GroupByMenuSettings, SelectColumnContent } from "..";
import { RoleName } from "../../../constants";
import { NumberFormat } from "../Number-format/NumberFormat";
import { SelectCurrency } from "../Select-currency/SelectCurrency";

const iconsSx: IconProps["sx"] = {
  fontSize: "16px",
  mr: "6px"
};

const isInTheFuture = (date: string): boolean => {
  const today = new Date();
  today.setHours(23, 59, 59, 998);
  return new Date(date) > today;
};

interface QueryManagerProps extends GroupByMenuSettings {
  riskMetrics?: boolean;
  showGroupByMenu?: boolean;
  showRowBy?: boolean;
  showCurrency?: boolean;
  showFilters?: boolean;
}

export const QueryManager: React.FC<QueryManagerProps> = ({
  showGroupByMenu = false,
  showCharacteristics = false,
  showColumns = false,
  showGroupBy = true,
  showRowBy = true,
  showCurrency = true,
  showFilters = true
}) => {
  const { dateRange, comparisonColumns, secondaryDateRange } = useReportsQueriesSelector();
  const { show_cashflow } = useAccountSelector() || {};
  const { disable_apply_filters, show_global_investor_filter } = useAccountSelector() || {};

  const stateFilters = useReportsFiltersSelector();
  const { pathname } = useLocation();
  const isCashflow = matchPath(pathname, Routes.cashflow);

  const { errorModal, noticeModal } = useModals();

  const { setQueriesAction, getFilters } = useReportsActions();
  const { updateQueries } = useDealActions();

  const { location } = useHistory();

  const handleDateRangeChange = useCallback(
    (data: DateRange & { value: DateRangeValueType }, use_accounting_date?: boolean) => {
      const { to, from } = data;
      use_accounting_date = use_accounting_date || false;
      if ((to && isInTheFuture(to)) || (from && isInTheFuture(from))) {
        noticeModal({
          msg: "Notice - Report can not show future values of different currency. Different currency's in future dates are ignored."
        });
      }

      if (to && from && from > to) {
        // eslint-disable-next-line quotes
        return errorModal({ msg: '"Date From" must be less than "Date To"' });
      }

      setQueriesAction({
        dateRange: data,
        use_accounting_date
      });
    },
    []
  );

  const { data: filtersList, action: fetchFilters } = useActionHook(getFilters);

  const getReportsFilters = useCallback(
    async (filters?: ReportFilters) => {
      await fetchFilters(filters || {});
    },
    [fetchFilters]
  );

  useEffect(() => {
    getReportsFilters(stateFilters);
  }, []);

  useEffect(() => {
    if (!Object.keys(stateFilters).length) return;
    getReportsFilters(stateFilters);
  }, [stateFilters]);

  return (
    <Stack
      sx={{
        alignItems: "stretch",
        color: "#0a3243",
        position: "sticky",
        top: 0,
        gap: "16px",
        padding: 0,
        marginBottom: "16px",
        // margin: "0 -5px",
        zIndex: 1
      }}
    >
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Stack direction="row" gap={"15px"} alignItems="center">
          <HorizontalNavBar
            tabs={[
              {
                value: "overview",
                label: "Overview",
                icon: <GridViewIcon sx={iconsSx} />,
                to: Routes.snapshot,
                matchRouts: [Routes.snapshot]
              },
              {
                value: "classic",
                label: "Classic",
                icon: <AnalyticsOutlinedIcon sx={iconsSx} />,
                to: Routes.classic,
                matchRouts: [Routes.classic]
              },
              {
                value: "timeline",
                label: "Timeline",
                icon: <InsightsOutlinedIcon sx={iconsSx} />,
                to: Routes.timeline,
                matchRouts: [Routes.timeline]
              },
              ...(show_cashflow
                ? [
                    {
                      value: "cashflow",
                      label: "Forecast",
                      icon: <InsertChartOutlinedIcon sx={iconsSx} />,
                      to: Routes.cashflow,
                      matchRouts: [Routes.cashflow]
                    }
                  ]
                : [])
            ]}
          />
        </Stack>
      </Stack>
      {showFilters && (
        <Stack
          flexDirection="row"
          justifyContent="space-between"
          sx={{
            width: "100%",
            bgcolor: "white",
            borderRadius: "8px",
            boxShadow: "0 1px 2px 0 #1212171a",
            alignItems: "stretch",
            p: "16px",
            maxHeight: "140px",
            overflow: "auto",
            "& > *": {
              flex: 1,
              maxWidth: "50%"
            }
          }}
        >
          <InlineFilter
            state={stateFilters}
            filtersList={filtersList || {}}
            handleChangeFilter={(filters) => {
              setQueriesAction({ filters });
              show_global_investor_filter && updateQueries({ filters });
            }}
            applyMode={!disable_apply_filters}
            refetchFilters={(filters) =>
              disable_apply_filters ? getReportsFilters(stateFilters) : getReportsFilters(filters)
            }
            {...(isCashflow && { beginningElement: <SetPicker /> })}
          />
          <Stack direction="row" sx={{ gap: "10px", alignItems: "center", justifyContent: "flex-end" }}>
            <NumberFormat />
            {location.pathname !== Routes.dealReport && (
              <SelectDateRange
                formControlProps={{ sx: { minWidth: "200px" } }}
                selectProps={{
                  startAdornment: <TodayOutlinedIcon sx={{ fontSize: "15px" }} />
                }}
                label=""
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            )}
            {showGroupByMenu && (
              <>
                <GroupByMenu>
                  {showColumns ? (
                    <SelectColumnContent
                      selectedColumns={comparisonColumns}
                      queriedDateRange={dateRange}
                      queriedSecondaryDateRange={secondaryDateRange}
                    />
                  ) : null}
                  <GroupByContent
                    showGroupBy={showGroupBy}
                    showRowBy={showRowBy}
                    showCharacteristics={showCharacteristics}
                  />
                </GroupByMenu>
              </>
            )}
            {showCurrency && (
              <>
                <SelectCurrency />
              </>
            )}
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <ContextMenuReports />
            </RoleComponentGuard>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
